import { useAdminText } from './shared/useAdminText';
import { readCostPeriod, costDateRange, costPeriodParams, isValidCostPeriod } from '../utils/aiCostPeriod';
import { AdminTable } from './shared/AdminDataGrid';
import { useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, LinearProgress, Link, MenuItem, Paper, Stack,
  TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,
  TextField, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { adminApi } from '../providers/dataProvider';
import { useAICostQuery } from '../hooks/useAICostQuery';
import {
  presentCostLog, presentRecordedCost, presentUsage, presentToolUsage,
  type AICostCall, type AICostCallFilters,
} from '../utils/aiCostPresentation';

function CallRow({ call }: { call: AICostCall }) {
  const [params] = useSearchParams();
  const t = useAdminText();
  const [expanded, setExpanded] = useState(false);
  const detailId = `ai-call-${call.id}`;
  const cost = presentCostLog(call.costUSD, call.costSource, t);
  return (
    <>
      <TableRow hover>
        <TableCell>
          <Typography variant="body2">{new Date(call.timestamp).toLocaleString()}</Typography>
          <Button size="small" aria-expanded={expanded} aria-controls={detailId} onClick={() => setExpanded(!expanded)}>
            {expanded ? 'Hide details' : 'Call details'}
          </Button>
        </TableCell>
        <TableCell>
          {call.userId ? (
            <Link component={RouterLink} to={`/ai-costs/${encodeURIComponent(call.userId)}?${costPeriodParams(params)}`}>
              {call.userEmail ?? call.userId}
            </Link>
          ) : 'System / unattributed'}
          {call.userName && <Typography variant="caption" display="block">{call.userName}</Typography>}
        </TableCell>
        <TableCell>
          <Typography variant="body2">{call.serviceName ?? 'Service not recorded'}</Typography>
          <Typography variant="caption" display="block">{call.provider ?? 'Provider not recorded'} · {call.model}</Typography>
          {call.operationId ? (
            <Link component={RouterLink} to={`/ai-costs?${costPeriodParams(params)}&operationId=${encodeURIComponent(call.operationId)}`}
              sx={{ overflowWrap: 'anywhere' }}>
              {call.operationId}
            </Link>
          ) : <Typography variant="caption" display="block">Operation not recorded</Typography>}
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2">{presentUsage(call.totalTokens, call.usageReported || call.costSource === 'legacy')}</Typography>
          <Typography variant="caption">Input {presentUsage(call.inputTokens, call.usageReported || call.costSource === 'legacy')} · Output {presentUsage(call.outputTokens, call.usageReported || call.costSource === 'legacy')}</Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2">{presentToolUsage(call.billableUnits, call.billingUnit)}</Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2">{cost.value}</Typography>
          <Typography variant="caption">{cost.detail}</Typography>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow id={detailId}>
          <TableCell colSpan={6}>
            <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'minmax(120px, auto) 1fr', columnGap: 2, '& dd': { m: 0, overflowWrap: 'anywhere' } }}>
              <Typography component="dt" variant="body2">Call ID</Typography><Typography component="dd" variant="body2">{call.id}</Typography>
              <Typography component="dt" variant="body2">Provider request ID</Typography><Typography component="dd" variant="body2">{call.providerRequestId ?? 'Not recorded'}</Typography>
              <Typography component="dt" variant="body2">Cached input tokens</Typography><Typography component="dd" variant="body2">{presentUsage(call.cachedTokens)}</Typography>
              <Typography component="dt" variant="body2">Cache write tokens</Typography><Typography component="dd" variant="body2">{presentUsage(call.cacheWriteTokens)}</Typography>
              <Typography component="dt" variant="body2">Reasoning tokens</Typography><Typography component="dd" variant="body2">{presentUsage(call.reasoningTokens)}</Typography>
            </Box>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

/** Shared global/user drill-down. Filtering by operation includes all calls for that turn/job. */
export function AICostCalls({ userId }: { userId?: string }) {
  const [params, setParams] = useSearchParams();
  const t = useAdminText();
  const period = readCostPeriod(params);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const filters: AICostCallFilters = {
    operationId: params.get('operationId') || undefined,
    serviceName: params.get('serviceName') || undefined,
    provider: params.get('provider') || undefined,
    attribution: !userId && params.get('attribution') === 'unattributed' ? 'unattributed' : 'all',
  };
  const [draft, setDraft] = useState<AICostCallFilters>(filters);
  const query = useAICostQuery(
    ['ai-costs', 'calls', { ...filters, userId }, period, page, rowsPerPage],
    () => adminApi.getAICostCalls(page * rowsPerPage, rowsPerPage, { ...filters, userId, ...costDateRange(period) }), isValidCostPeriod(period),
  );
  const result = query.data;
  const cost = result && presentRecordedCost(result.summary.recordedCostUSD, result.summary.costCoverage, t);
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <Typography variant="h6" sx={{ flex: 1 }}>{userId ? 'All calls for this user' : 'All AI calls'}</Typography>
        <Button startIcon={<RefreshIcon />} onClick={() => void query.refetch()} disabled={query.isFetching}>Refresh</Button>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        Includes recorded chat, moderation, routing, embeddings, external tools and background work. One row is one provider call.
        Select an operation ID to see all calls for a turn or job. Historical records may lack attribution.
        Tool credits describe usage; they do not imply a known USD cost.
      </Typography>
      <Box component="form" onSubmit={event => {
        event.preventDefault();
        const next = costPeriodParams(params);
        for (const [key, value] of Object.entries(draft)) if (value?.trim()) next.set(key, value.trim());
        setParams(next);
        setPage(0);
      }}>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
          {!userId && <TextField select size="small" label="Attribution" sx={{ minWidth: 190 }}
            value={draft.attribution ?? 'all'} onChange={event => setDraft({ ...draft, attribution: event.target.value as 'all' | 'unattributed' })}>
            <MenuItem value="all">All calls</MenuItem><MenuItem value="unattributed">System / unattributed</MenuItem>
          </TextField>}
          {(['operationId', 'serviceName', 'provider'] as const).map(field => (
            <TextField key={field} size="small" label={{ operationId: 'Operation ID', serviceName: 'Service', provider: 'Provider' }[field]}
              value={draft[field] ?? ''} onChange={event => setDraft({ ...draft, [field]: event.target.value })}
              slotProps={{ htmlInput: { maxLength: field === 'operationId' ? 256 : 128 } }} />
          ))}
          <Button type="submit" variant="outlined">Apply filters</Button>
          <Button onClick={() => { setDraft({}); setParams(costPeriodParams(params)); setPage(0); }}>Clear</Button>
        </Stack>
      </Box>
      {query.isError && <Alert severity="error" action={<Button color="inherit" onClick={() => void query.refetch()}>Retry</Button>}>
        {query.accessDenied ? 'Access to AI costs was denied. Reauthenticate before retrying.' : result ? 'Could not refresh calls. Showing the last successful result.' : 'Could not load AI calls.'}
      </Alert>}
      {query.isFetching && <LinearProgress aria-label="Loading AI calls" />}
      {result && cost && (
        <>
          <Card><CardContent>
            <Typography variant="h6">Recorded cost: {cost.value} · {result.total.toLocaleString()} calls</Typography>
            <Typography variant="body2">{cost.detail}</Typography>
            <Typography variant="body2">
              {result.summary.reportedTotalTokens.toLocaleString()} reported tokens
              {' '}({result.summary.reportedInputTokens.toLocaleString()} input / {result.summary.reportedOutputTokens.toLocaleString()} output)
              {' · '}{result.summary.unattributedCalls.toLocaleString()} system / unattributed calls.
              {' '}Totals cover all matching calls, across every page.
            </Typography>
          </CardContent></Card>
          <Typography variant="caption" color="text.secondary" role="status">
            Updated {new Date(result.generatedAt).toLocaleString()}. Refreshes every 15 seconds while this page is visible and on window focus.
          </Typography>
          <TableContainer component={Paper}>
            <AdminTable size="small" aria-label="AI provider calls">
              <TableHead><TableRow>
                <TableCell>Recorded at</TableCell><TableCell>User</TableCell>
                <TableCell>Service / provider / model / operation</TableCell>
                <TableCell align="right">{t('recordedTokens', 'Recorded tokens')}</TableCell><TableCell align="right">Tool usage</TableCell><TableCell align="right">Recorded cost</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {result.data.map(call => <CallRow key={call.id} call={call} />)}
                {result.data.length === 0 && <TableRow><TableCell colSpan={6}>No calls match these filters.</TableCell></TableRow>}
              </TableBody>
            </AdminTable>
            <TablePagination component="div" count={result.total} page={page} rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]} onPageChange={(_, next) => setPage(next)}
              onRowsPerPageChange={event => { setRowsPerPage(Number(event.target.value)); setPage(0); }} />
          </TableContainer>
        </>
      )}
    </Stack>
  );
}
